<template>
  <div v-if="paypalUuid" :id="'paypal-' + paypalUuid" ref="paypalButton" class="z-base relative paypal-button" />
</template>

<script setup lang="ts">
import { cartGetters } from '@plentymarkets/shop-api';
import type { PayPalNamespace, FUNDING_SOURCE, OnApproveData, OnInitActions } from '@paypal/paypal-js';
import type { PayPalAddToCartCallback, PaypalButtonPropsType } from '../types';
import { usePayPal } from '../composables/usePayPal';

const paypalButton = ref<HTMLElement | null>(null);
const paypalUuid = ref(useId());
const paypalScript = ref<PayPalNamespace | null>(null);

const {
  order: paypalOrder,
  isAvailable,
  getScript,
  loadConfig,
  createTransaction,
  captureOrder,
  createPlentyOrder,
  createPlentyPaymentFromPayPalOrder,
  resetAPMs,
  config,
  payPalVisibility,
  payLaterVisibility,
} = usePayPal();
const { data: cart, clearCartItems } = useCart();
const { fetchSession } = useFetchSession();
const { emit } = usePlentyEvent();

const currency = computed(
  () => props.currency || cartGetters.getCurrency(cart.value) || (useAppConfig().fallbackCurrency as string),
);
const localePath = useLocalizedPath();

const emits = defineEmits<{
  (event: 'validation-callback', callback: PayPalAddToCartCallback): Promise<void>;
  (event: 'on-approved' | 'on-payed'): void;
}>();

const props = defineProps<PaypalButtonPropsType>();
const currentInstance = getCurrentInstance();

const TypeCartPreview = 'CartPreview';
const TypeSingleItem = 'SingleItem';
const TypeCheckout = 'Checkout';
const TypeOrderAlreadyExisting = 'OrderAlreadyExisting';

const isCommit = props.type === TypeCheckout || props.type === TypeOrderAlreadyExisting;
const loadScript = computed(
  () => payPalVisibility.getVisibility(props.location) || payLaterVisibility.getVisibility(props.location),
);

const checkonValidationCallbackEvent = (): boolean => {
  const props = currentInstance?.vnode.props;

  return !!(props && props['onValidationCallback']);
};

const onInit = (actions: OnInitActions) => {
  if (props.type === TypeCheckout) {
    props.disabled ? actions.disable() : actions.enable();
    watch(props, (propertyValues) => {
      if (propertyValues.disabled) {
        actions.disable();
      } else {
        actions.enable();
      }
    });
  } else {
    actions.enable();
  }
};

const onValidationCallback = async () => {
  return await new Promise<boolean>((resolve) => {
    if (!checkonValidationCallbackEvent()) {
      resolve(true);
    }
    emits('validation-callback', async (successfully) => {
      resolve(successfully);
    });
  });
};

const onApprove = async (data: OnApproveData) => {
  emits('on-approved');
  resetAPMs();

  if (props.type === TypeCartPreview || props.type === TypeSingleItem) {
    await fetchSession();
    navigateTo(localePath(paths.readonlyCheckout + `/?payerId=${data.payerID}&orderId=${data.orderID}`));
  }

  if (props.type === TypeCheckout) {
    useDynamicPaymentButtons().createOrderLoading.value = true;
    const order = await createPlentyOrder();

    if (order) {
      if (!paypalOrder.value?.isAutocaptured) {
        await captureOrder(data.orderID);
      }
      await createPlentyPaymentFromPayPalOrder(data.orderID, order.order.id);
    }

    emit('module:clearCart', null);
    clearCartItems();

    if (order?.order?.id) {
      emit('frontend:orderCreated', order);
      navigateTo(localePath(paths.confirmation + '/' + order.order.id + '/' + order.order.accessKey));
    }
  } else if (props.type === TypeOrderAlreadyExisting && props.plentyOrderId) {
    if (!paypalOrder.value?.isAutocaptured) {
      await captureOrder(data.orderID);
    }
    await createPlentyPaymentFromPayPalOrder(data.orderID, props.plentyOrderId);
    emits('on-payed');
  }
};

const getLabel = (type: string) => {
  switch (type) {
    case TypeCartPreview:
    case TypeSingleItem:
      return 'checkout';
    case TypeOrderAlreadyExisting:
      return 'pay';
    default:
      return 'buynow';
  }
};

const renderButton = (fundingSource: FUNDING_SOURCE) => {
  if (paypalScript.value?.Buttons && fundingSource) {
    const button = paypalScript.value?.Buttons({
      style: {
        layout: 'vertical',
        label: getLabel(props.type),
        color: 'gold',
      },
      fundingSource: fundingSource,
      async onClick(data, actions) {
        const success = await onValidationCallback();
        if (!success) {
          return actions.reject();
        }
        return actions.resolve();
      },
      onInit(data, actions) {
        onInit(actions);
      },
      async onCancel() {
        useNotification().send({
          message: t('error.paymentCancelled'),
          type: 'negative',
        });
        resetAPMs();
        await fetchSession();
        await useCartStockReservation().unreserve();
      },
      async createOrder() {
        let transactionType: 'order' | 'express' | 'basket' = 'express';

        if (props.type === TypeOrderAlreadyExisting) {
          transactionType = 'order';
        } else if (isCommit) {
          transactionType = 'basket';
        }

        const order = await createTransaction({
          type: transactionType,
          withShippingCallback: props.type !== TypeOrderAlreadyExisting && !isCommit,
          ...(props.type === TypeOrderAlreadyExisting && { plentyOrderId: props.plentyOrderId }),
        });

        if (order?.id && props.type !== TypeOrderAlreadyExisting) {
          const reserved = await useCartStockReservation().reserve();
          if (!reserved) return '';
        }

        if (props.type === TypeCartPreview || props.type === TypeSingleItem) {
          useLogEvent().logPayPalExpressFlow();
        }

        return order?.id ?? '';
      },
      async onApprove(data) {
        await onApprove(data);
      },
    });

    if (button.isEligible() && paypalButton.value) button.render('#' + paypalButton.value.id);
  }
};

const createButton = () => {
  if (paypalScript.value) {
    if (paypalButton.value) {
      paypalButton.value.innerHTML = '';
    }

    if (paypalScript.value.FUNDING) {
      const FUNDING_SOURCES: Array<string> = [];

      if (payPalVisibility.getVisibility(props.location ?? 'checkoutPage')) {
        FUNDING_SOURCES.push(paypalScript.value.FUNDING.PAYPAL as string);
      }
      if (payLaterVisibility.getVisibility(props.location ?? 'checkoutPage')) {
        FUNDING_SOURCES.push(paypalScript.value.FUNDING.PAYLATER as string);
      }

      FUNDING_SOURCES.forEach((fundingSource) => renderButton(fundingSource as FUNDING_SOURCE));
    }
  }
};

onNuxtReady(async () => {
  await loadConfig();
  if (!config.value || !isAvailable(props.location ?? 'checkoutPage').value) return;
  paypalScript.value = await getScript(currency.value, isCommit);
  createButton();

  watch([currency, loadScript], async () => {
    if (!loadScript.value || !config.value || !isAvailable(props.location ?? 'checkoutPage').value) return;
    paypalScript.value = await getScript(currency.value, isCommit);
    createButton();
  });
});
</script>
